// Load exchange rates script
if (!document.querySelector('script[src*="exchange-rates.js"]')) {
  const script = document.createElement('script');
  script.src = window.theme && window.theme.assets_url 
    ? window.theme.assets_url + '/exchange-rates.js' 
    : '/assets/exchange-rates.js';
  script.defer = true;
  document.head.appendChild(script);
}

if (!customElements.get('currency-form-custom')) {
  class CurrencyFormCustom extends HTMLElement {
    connectedCallback() {
      // Cache elements
      this.els = {
        button: this.querySelector('button'),
        panel: this.querySelector('ul'),
        currentCurrency: this.querySelector('.current-currency'),
        items: Array.from(this.querySelectorAll('a'))
      };

      // Events
      this.onToggle = this.onToggle.bind(this);
      this.onOutside = this.onOutside.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onItem = this.onItem.bind(this);
      this.onStorage = this.onStorage.bind(this);
      this.onExchangeRatesUpdated = this.onExchangeRatesUpdated.bind(this);

      this.els.button?.addEventListener('click', this.onToggle);
      document.addEventListener('click', this.onOutside);
      this.addEventListener('keydown', this.onKey);
      this.els.items.forEach(a => a.addEventListener('click', this.onItem));
      window.addEventListener('storage', this.onStorage); 
      document.addEventListener('exchange:rates:updated', this.onExchangeRatesUpdated);

      // Initialize exchange rates manager first
      this.initExchangeRatesManager().then(() => {
        // Update rates from API immediately
        this.updateRatesFromApi();
        // Then sync from storage
        this.syncFromStorage();
      });
    }

    /**
     * Initialize the exchange rates manager
     */
    async initExchangeRatesManager() {
      if (typeof window.exchangeRatesManager === 'undefined') {
        if (typeof ExchangeRatesManager !== 'undefined') {
          window.exchangeRatesManager = new ExchangeRatesManager({
            baseCurrency: 'EUR',
            cacheDuration: 86400000 // 24 hours
          });
          // Wait for initial fetch to complete
          try {
            await window.exchangeRatesManager.fetchRates();
          } catch (error) {
            console.error('Error fetching initial rates:', error);
          }
        } else {
          // Wait for ExchangeRatesManager to be defined
          return new Promise(resolve => {
            const checkInterval = setInterval(() => {
              if (typeof ExchangeRatesManager !== 'undefined') {
                clearInterval(checkInterval);
                window.exchangeRatesManager = new ExchangeRatesManager({
                  baseCurrency: 'EUR',
                  cacheDuration: 86400000 // 24 hours
                });
                resolve();
              }
            }, 200);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkInterval);
              resolve();
            }, 5000);
          });
        }
      }
      return Promise.resolve();
    }

    /**
     * Update rates from API
     */
    updateRatesFromApi() {
      if (!window.exchangeRatesManager) return;
      
      // Update all currency items with rates from API
      this.els.items.forEach(a => {
        const code = (a.dataset.code || '').toUpperCase();
        if (code) {
          const apiRate = window.exchangeRatesManager.getRate(code);
          if (apiRate && apiRate > 0) {
            // Update the data-rate attribute with the latest rate
            a.dataset.rate = apiRate.toString();
            console.log(`Updated rate for ${code}: ${apiRate}`);
          }
        }
      });
    }

    disconnectedCallback() {
      this.els?.button?.removeEventListener('click', this.onToggle);
      document.removeEventListener('click', this.onOutside);
      this.removeEventListener('keydown', this.onKey);
      this.els?.items?.forEach(a => a.removeEventListener('click', this.onItem));
      window.removeEventListener('storage', this.onStorage);
      document.removeEventListener('exchange:rates:updated', this.onExchangeRatesUpdated);
    }

    getOptionByCode(code) {
      const uc = (code || '').toUpperCase();
      return this.els.items.find(a => (a.dataset.code || '').toUpperCase() === uc) || null;
    }

    getPreferredDefaultOption() {
      // 1) aria-current option (if you mark one), 2) matches .current-currency text, 3) first option
      const ariaCurrent = this.els.items.find(a => a.getAttribute('aria-current') === 'true');
      if (ariaCurrent) return ariaCurrent;
      const currentText = (this.els.currentCurrency?.textContent || '').trim().toUpperCase();
      const matchByText = this.getOptionByCode(currentText);
      return matchByText || this.els.items[0] || null;
    }

    getSaved() {
      try { return JSON.parse(localStorage.getItem('currency') || 'null'); }
      catch { return null; }
    }

    setSavedFromOption(opt, { dispatch = true } = {}) {
      if (!opt) return;
      
      // Get currency code
      const code = (opt.dataset.code || '').toUpperCase();
      
      // Try to get rate from Exchange Rates API if available
      let rate = parseFloat(opt.dataset.rate || '1') || 1;
      
      // If exchange rates manager is available, use it to get the latest rate
      if (window.exchangeRatesManager) {
        const apiRate = window.exchangeRatesManager.getRate(code);
        if (apiRate && apiRate > 0) {
          rate = apiRate;
          // Update the data-rate attribute with the latest rate
          opt.dataset.rate = apiRate.toString();
        }
      }
      
      const chosen = {
        code: code,
        rate: rate,
        label: opt.dataset.label || opt.dataset.code || ''
      };
      localStorage.setItem('currency', JSON.stringify(chosen));     // 🔧 ensure we always save here
      this.els.currentCurrency && (this.els.currentCurrency.textContent = chosen.code);
      if (dispatch) {
        document.dispatchEvent(new CustomEvent('currency:changed', { detail: chosen }));
      }
      // Optional: reflect selection state for a11y
      this.els.items.forEach(a => a.setAttribute('aria-current',
        (a === opt) ? 'true' : 'false'));
    }

    // 🔧 Core normalization step: called on connect and when options/rates change
    syncFromStorage() {
      const saved = this.getSaved();
      const codes = new Set(this.els.items.map(a => (a.dataset.code || '').toUpperCase()));

      if (!saved || !saved.code || !codes.has((saved.code || '').toUpperCase())) {
        // Saved code is missing or no longer available → pick a sensible default and broadcast
        const def = this.getPreferredDefaultOption();
        if (def) {
          this.setSavedFromOption(def, { dispatch: true });
        } else {
          // No options at all → clear storage to avoid inconsistent state
          localStorage.removeItem('currency');
        }
        return;
      }

      // Saved code exists; ensure rate is up-to-date with current option
      const opt = this.getOptionByCode(saved.code);
      const latestRate = parseFloat(opt.dataset.rate || '1') || 1;
      if (saved.rate !== latestRate || (this.els.currentCurrency && this.els.currentCurrency.textContent !== saved.code)) {
        // Update storage + UI and notify
        this.setSavedFromOption(opt, { dispatch: true });
      } else {
        // Keep UI label in sync without rebroadcasting
        this.els.currentCurrency && (this.els.currentCurrency.textContent = saved.code);
        // Keep aria-current
        this.els.items.forEach(a => a.setAttribute('aria-current',
          (a === opt) ? 'true' : 'false'));
      }
    }

    onStorage(e) {
      if (e.key === 'currency') {
        // Cross-tab/cross-component sync
        this.syncFromStorage();
      }
    }

    // Handle when exchange rates are updated
    onExchangeRatesUpdated(e) {
      if (e.detail && e.detail.rates) {
        // Update data-rate attributes with new rates
        this.els.items.forEach(a => {
          const code = (a.dataset.code || '').toUpperCase();
          if (code && e.detail.rates[code]) {
            a.dataset.rate = e.detail.rates[code].toString();
            console.log(`Exchange rates updated for ${code}: ${e.detail.rates[code]}`);
          }
        });
        
        // Re-sync from storage to update current currency if needed
        this.syncFromStorage();
      }
    }

    get isOpen() {
      return this.els.button?.getAttribute('aria-expanded') === 'true';
    }

    open() {
      if (!this.els.panel) return;
      this.els.panel.removeAttribute('hidden');
      this.els.panel.setAttribute('aria-hidden', 'false');
      this.els.button?.setAttribute('aria-expanded', 'true');
      // Make sure panel is focusable for keyboard control
      if (!this.els.panel.hasAttribute('tabindex')) this.els.panel.setAttribute('tabindex', '-1');
      this.els.panel.focus();
    }

    close() {
      if (!this.els.panel) return;
      this.els.panel.setAttribute('hidden', '');
      this.els.panel.setAttribute('aria-hidden', 'true');
      this.els.button?.setAttribute('aria-expanded', 'false');
    }

    onToggle(e) {
      e.preventDefault();
      this.isOpen ? this.close() : this.open();
    }

    onOutside(e) {
      if (!this.contains(e.target)) this.close();
    }

    onKey(e) {
      if (!this.isOpen) return;
      const idx = this.els.items.findIndex(a => a === document.activeElement);

      switch (e.key) {
        case 'Escape':
          this.close();
          this.els.button?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          (this.els.items[idx + 1] || this.els.items[0])?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          (this.els.items[idx - 1] || this.els.items[this.els.items.length - 1])?.focus();
          break;
        case 'Enter':
          if (document.activeElement?.dataset?.code) {
            e.preventDefault();
            this.pick(document.activeElement.dataset);
          }
          break;
      }
    }

    onItem(e) {
      e.preventDefault();
      this.pick(e.currentTarget.dataset);
    }

    pick(dataset) {
      const code = (dataset.code || '').toUpperCase();
      const opt = this.getOptionByCode(code);
      // 🔧 Always set from the canonical option to ensure freshest rate/label
      if (opt) {
        this.setSavedFromOption(opt, { dispatch: true });
      } else {
        // Option disappeared between click and handling; re-sync to a default
        this.syncFromStorage();
      }
      this.close();
    }
  }

  customElements.define('currency-form-custom', CurrencyFormCustom);
}